import { useEffect, useLayoutEffect, useRef, useState, type JSX } from "react";
import { AcceleratorDocMetadata } from "../../models/AcceleratorDocMetadata";
import {
  DocumentStatus,
  getDocumentStatuses,
  getDocumentStatusesDisplayValue,
  isDocumentStatus,
} from "../../types/DocumentStatuses";
import { Bubble } from "../bubbles/Bubble";
import { DocumentStatusBubble } from "../bubbles/DocumentStatusBubble";
import Icon from "../Icon";
import { useBatchLockState } from "../locking/contexts/GoodLockProvider";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import {
  TTCollapseAllBatches,
  TTSelectAllBatches,
} from "../tooltips/TTDocumentSelector";
import "../../styles/circles.css";
import "../../styles/documentSelector/documentSelectorList.css";
import "../../styles/loading.css";

type Props = {
  filtersCount: { [x: string]: number };
  metadata: AcceleratorDocMetadata[];
  /**
   * True if all batches collapsed
   */
  isAllCollapsed: boolean;
  /** Collapsed Batch UUIDs */
  collapsedBatches: string[];
  /** Selected Batch UUIDs. NOTE: this will likely only contain the current metadata for the DocStatus filter */
  selectedBatches: string[];
  /** toggle selection of batches. pass true/false to set a specific value. */
  toggleSelectAll: (value?: boolean, filter?: DocumentStatus) => void;
  /** toggle collapsed of batches. pass true/false to set a specific value */
  toggleCollapseAll: (value?: boolean) => void;
  /**
   * Change the active AcceleratorBatch status filter.
   */
  changeFilter: (filter: DocumentStatus | undefined) => void;
  /**
   * Render a Group component above each Item.
   */
  renderGroup: (
    metadata: AcceleratorDocMetadata,
    lockedBy: string | undefined,
    groupsLength: number,
  ) => JSX.Element;
  /**
   * Item component, many rendered for each Group.
   */
  renderItem: (metadata: AcceleratorDocMetadata) => JSX.Element;
};

const DocumentSelectorList = (props: Props) => {
  const mounted = useRef(false);
  const itemsRef = useRef<HTMLDivElement | null>(null);
  const [activeFilter, setActiveFilter] = useState<string | undefined>(
    undefined,
  );
  // const lockState = useLockState();
  const lockState = useBatchLockState();

  useEffect(() => {
    if (mounted.current) {
      props.changeFilter(activeFilter as DocumentStatus | undefined);
      // reset selected groups when filter changes
      props.toggleSelectAll(false, activeFilter as DocumentStatus | undefined);
      props.toggleCollapseAll(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    mounted.current = true;
  }, []);
  /**
   * Update index after rendering
   */
  useLayoutEffect(() => {
    if (!itemsRef?.current) return;
    const indexNodes = itemsRef.current.querySelectorAll(
      ".documentSelectorItem>div.box-section.left>strong",
    );
    indexNodes.forEach((x, i) => (x.textContent = i + 1 + ""));
  });

  // metadata must be ordered so groups are together. this loops enforces that
  const groups2: AcceleratorDocMetadata[][] = [];
  for (const metadata of props.metadata) {
    if (groups2.length == 0) {
      groups2.push([metadata]);
    } else if (
      groups2[groups2.length - 1][0]?.storeId.batchInstanceUuid ===
      metadata.storeId.batchInstanceUuid
    ) {
      groups2[groups2.length - 1].push(metadata);
    } else {
      groups2.push([metadata]);
    }
  }

  // metadatas to render
  const visibleGroups = activeFilter
    ? groups2.filter((m) =>
        m.find((x) => x.statuses.find((status) => status === activeFilter)),
      )
    : groups2;

  // filteer metadata by

  // <= to support when changing filters
  const allVisibleSelected =
    visibleGroups.length <= props.selectedBatches.length;

  return (
    <div className={"documentSelectorList"}>
      <div className={"header"}>
        {getDocumentStatuses().map((filter) => (
          <DocStatusFilterButton
            key={filter}
            filter={filter}
            activeFilter={activeFilter ?? "All"}
            filterCount={props.filtersCount[filter]}
            setFilter={setActiveFilter}
          />
        ))}
        <DocStatusFilterButton
          filter={"All"}
          activeFilter={activeFilter ?? "All"}
          filterCount={props.metadata.length}
          setFilter={() => setActiveFilter(undefined)}
        />
      </div>
      {groups2.length > 1 && (
        <>
          <div className={"selectBatches"}>
            <AdvancedTooltip tooltipText={TTSelectAllBatches}>
              {(tProps) => (
                <input
                  {...tProps}
                  type={"checkbox"}
                  checked={allVisibleSelected}
                  onChange={() =>
                    props.toggleSelectAll(
                      !allVisibleSelected,
                      activeFilter as DocumentStatus | undefined,
                    )
                  }
                />
              )}
            </AdvancedTooltip>
            <AdvancedTooltip short tooltipText={TTCollapseAllBatches}>
              {(tProps) => (
                <Icon
                  {...tProps}
                  onClick={() => props.toggleCollapseAll()}
                  className={"collapsed"}
                  altText={""}
                  icon={props.isAllCollapsed ? "chevron-left" : "chevron-down"}
                />
              )}
            </AdvancedTooltip>
          </div>
        </>
      )}
      <div className={"items"} ref={itemsRef}>
        {visibleGroups.map((metadataGroup, i) => {
          // metadatas to render
          const visibleMetadatas = activeFilter
            ? metadataGroup.filter((m) =>
                m.statuses.find((status) => status === activeFilter),
              )
            : metadataGroup;
          // if no metadatas, dont render group either
          if (visibleMetadatas.length === 0) {
            return null;
          }

          const isStale = visibleMetadatas.filter((x) => x.stale).length > 0;
          const collapsed = props.collapsedBatches.includes(
            metadataGroup[0].storeId.batchInstanceUuid,
          );

          visibleMetadatas.forEach((x) => (x.stale = isStale));
          return (
            <div key={metadataGroup[0].storeId.batchInstanceUuid + i}>
              {props.renderGroup(
                metadataGroup[0],
                (lockState[metadataGroup[0]?.storeId.batchInstanceUuid] ??
                  [])[0],
                groups2.length,
              )}

              {(groups2.length === 1 || !collapsed) &&
                visibleMetadatas.map((metadata) => props.renderItem(metadata))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

type DocStatusFilterButtonProps = {
  /* Active DocStatus filter */
  activeFilter?: string;
  /* DocStatus Filter to render */
  filter: DocumentStatus | "All";
  /** Number of Documents with this status */
  filterCount?: number;
  /* Make this filter the active filter */
  setFilter: (filter: DocumentStatus | "All") => void;
};
const DocStatusFilterButton = ({
  activeFilter,
  filter,
  filterCount,
  setFilter,
}: DocStatusFilterButtonProps) => {
  return (
    <button
      key={filter}
      onClick={() => setFilter(filter)}
      className={activeFilter === filter ? "active" : ""}
    >
      {isDocumentStatus(filter)
        ? getDocumentStatusesDisplayValue(filter)
        : filter}
      {isDocumentStatus(filter) && (
        <DocumentStatusBubble status={filter} text={"" + (filterCount ?? 0)} />
      )}
      {!isDocumentStatus(filter) && (
        <Bubble
          text={"" + (filterCount ?? 0)}
          className={""}
          style={{ marginLeft: "2rem" }}
        />
      )}
    </button>
  );
};

export { DocumentSelectorList };
